import {
  Alert,
  Chip,
  Divider,
  Drawer,
  Group,
  IconButton,
  iconSizes,
  Loader,
  NavLink,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@common/ui'
import { IconCopy, IconX } from '@common/ui/icons'
import { useEffect, useRef, useState } from 'react'
import { com } from '@eidu/web'
import useRegistryAssets from '../../io/useRegistryAssets'
import { RegistryAsset } from '../../io/api/registry/listRegistryAssets'
import useRegistryAsset from '../../io/useRegistryAsset'
import { useCompactNavigation } from '../../components/layout/CompactNavigationContext'
import PublishableEntityKinds = com.eidu.entity.publish.PublishableEntityKinds

const KIND_OPTIONS = ['', ...Array.from(PublishableEntityKinds.asJsReadonlySetView()).map((k) => k.toString())]

const formatDate = (ms: number) => new Date(ms).toLocaleString()

type SelectedAssetVersionsProps = {
  entityId: string
  onCopyReference: (entityId: string, versionId: string) => void
}

const SelectedAssetVersions = ({ entityId, onCopyReference }: SelectedAssetVersionsProps) => {
  const { detail, isLoading: detailLoading, error: detailError } = useRegistryAsset(entityId)
  if (detailLoading) return <Loader size="xs" />
  if (detailError) return <Alert severity="negative">{(detailError as Error).message}</Alert>
  if (!detail) return null
  return (
    <Stack gap={0}>
      {detail.versions.map((v) => (
        <Group key={v.versionId} justify="space-between" wrap="nowrap" py="xxs">
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="sm">{`${v.versionId.slice(0, 8)}…`}</Text>
            <Text size="xs" c="dimmed">
              {`${formatDate(v.createdAt)} · ${v.dependencyCount} deps`}
            </Text>
          </Stack>
          <Tooltip label="Copy asset reference">
            <IconButton aria-label="Copy asset reference" onClick={() => onCopyReference(detail.entityId, v.versionId)}>
              <IconCopy size={iconSizes.sm} stroke={1.5} />
            </IconButton>
          </Tooltip>
        </Group>
      ))}
    </Stack>
  )
}

type AssetDetailBodyProps = {
  asset: RegistryAsset
  copied: string | null
  onCopyReference: (entityId: string, versionId: string) => void
}

const AssetDetailBody = ({ asset, copied, onCopyReference }: AssetDetailBodyProps) => (
  <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
    <Text size="xs" style={{ flexShrink: 0 }}>
      Entity {asset.entityId}
    </Text>
    <Text size="xs" style={{ flexShrink: 0 }}>
      Log {asset.logId}
    </Text>
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <SelectedAssetVersions entityId={asset.entityId} onCopyReference={onCopyReference} />
    </div>
    {copied && (
      <Alert severity="positive" style={{ flexShrink: 0 }}>
        Copied {copied}
      </Alert>
    )}
  </Stack>
)

const RegistryPage = () => {
  const { isCompact } = useCompactNavigation()
  const [search, setSearch] = useState('')
  const [kind, setKind] = useState<string>('')
  const [selected, setSelected] = useState<RegistryAsset | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const { assets, loadMore, hasMore, loading, error } = useRegistryAssets({
    search: search.trim() || undefined,
    kind: kind || undefined,
  })

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, loading, loadMore])

  const onCopyReference = async (entityId: string, versionId: string) => {
    const ref = `asset://${entityId}@${versionId}`
    await navigator.clipboard.writeText(ref)
    setCopied(ref)
    window.setTimeout(() => setCopied(null), 1500)
  }

  return (
    <Group gap="md" align="stretch" wrap="nowrap" p="md" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Stack gap="xs" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
          <TextInput
            label="Search"
            size="sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            label="Kind"
            size="sm"
            value={kind}
            onChange={(value) => setKind(value ?? '')}
            data={KIND_OPTIONS.map((k) => ({ value: k, label: k || 'Any' }))}
            w={160}
          />
        </Group>
        {error && <Alert severity="negative">{error.message}</Alert>}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            border: '1px solid var(--trellis-color-outline)',
            borderRadius: 'var(--trellis-radius-sm)',
          }}
        >
          <Stack gap={0}>
            {assets.map((a) => (
              <NavLink
                key={a.entityId}
                component="button"
                active={selected?.entityId === a.entityId}
                onClick={() => setSelected(a)}
                label={
                  <Group gap="xs" wrap="nowrap">
                    <Text size="sm">{a.name}</Text>
                    <Chip size="sm">{a.kind}</Chip>
                  </Group>
                }
                description={`Org ${a.publishingOrgId.slice(0, 8)}… · first published ${formatDate(a.firstPublishedAt)}`}
              />
            ))}
            <div ref={sentinelRef} />
            {loading && <Loader size="xs" m="xs" />}
            {!loading && assets.length === 0 && (
              <Text size="sm" c="dimmed" p="xs">
                No published assets.
              </Text>
            )}
          </Stack>
        </div>
      </Stack>
      {!isCompact && selected && (
        <>
          <Divider orientation="vertical" />
          <Stack gap="xs" w={360} style={{ minHeight: 0 }} data-testid="registry-detail-panel">
            <Group justify="space-between" align="flex-start" wrap="nowrap" style={{ flexShrink: 0 }}>
              <Title order={4}>{selected.name}</Title>
              <IconButton aria-label="Close details" onClick={() => setSelected(null)}>
                <IconX size={iconSizes.sm} stroke={1.5} />
              </IconButton>
            </Group>
            <AssetDetailBody asset={selected} copied={copied} onCopyReference={onCopyReference} />
          </Stack>
        </>
      )}
      {isCompact && (
        <Drawer
          opened={!!selected}
          onClose={() => setSelected(null)}
          position="right"
          title={selected?.name}
          data-testid="registry-detail-drawer"
        >
          {selected && <AssetDetailBody asset={selected} copied={copied} onCopyReference={onCopyReference} />}
        </Drawer>
      )}
    </Group>
  )
}

export default RegistryPage
